import { useCallback, useMemo, useState } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { Text, TextField } from "@radix-ui/themes";
import { Check, Minus } from "lucide-react";
import { TreeNode, CheckState, ResponseFieldTreeProps } from "./ResponseField.model";
import styles from "./ResponseFieldTree.module.scss";

function buildTree(
  data: unknown,
  path: string,
  key: string
): TreeNode | null {
  if (data === null || data === undefined) {
    return { key, path, type: "null", sampleValue: "null" };
  }
  if (Array.isArray(data)) {
    const first = data[0];
    if (first != null && typeof first === "object" && !Array.isArray(first)) {
      const children = Object.entries(first as Record<string, unknown>)
        .map(([k, v]) => buildTree(v, path ? `${path}.${k}` : k, k))
        .filter((n): n is TreeNode => n != null);
      return { key, path, type: "array", children, isArray: true };
    }
    return { key, path, type: "array", isArray: true };
  }
  if (typeof data === "object") {
    const children = Object.entries(data as Record<string, unknown>)
      .map(([k, v]) => buildTree(v, path ? `${path}.${k}` : k, k))
      .filter((n): n is TreeNode => n != null);
    if (children.length === 0) return { key, path, type: "object" };
    return {
      key,
      path,
      type: "object",
      children,
      sampleValue: `{ ${children.length} fields }`,
    };
  }
  return { key, path, type: typeof data, sampleValue: String(data) };
}

function getLeafPaths(node: TreeNode): string[] {
  if (!node.children?.length) return [node.path];
  return node.children.flatMap(getLeafPaths);
}

function getCheckState(node: TreeNode, selected: Set<string>): CheckState {
  const leaves = getLeafPaths(node);
  const count = leaves.filter((p) => selected.has(p)).length;
  if (count === 0) return "unchecked";
  if (count === leaves.length) return "checked";
  return "indeterminate";
}

function matchesSearch(node: TreeNode, query: string): boolean {
  if (!query) return true;
  if (node.key.toLowerCase().includes(query)) return true;
  return (
    node.children?.some((child) => matchesSearch(child, query)) ?? false
  );
}

const GRID_COLUMNS = "minmax(0, 1.5fr) 80px minmax(0, 2fr)";

function FieldRow({
  node,
  selected,
  onToggle,
  depth,
  collapsed,
  onToggleCollapse,
  searchQuery,
}: {
  node: TreeNode;
  selected: Set<string>;
  onToggle: (node: TreeNode) => void;
  depth: number;
  collapsed: Set<string>;
  onToggleCollapse: (path: string) => void;
  searchQuery: string;
}) {
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = hasChildren && (!collapsed.has(node.path) || searchQuery !== "");
  const checkState = useMemo(
    () => getCheckState(node, selected),
    [node, selected]
  );
  const handleCheck = useCallback(() => onToggle(node), [onToggle, node]);
  const handleExpand = useCallback(
    () => onToggleCollapse(node.path),
    [onToggleCollapse, node.path]
  );

  const checkedValue = useMemo(
    () =>
      checkState === "checked"
        ? true
        : checkState === "indeterminate"
          ? ("indeterminate" as const)
          : false,
    [checkState]
  );

  if (!matchesSearch(node, searchQuery)) return null;

  return (
    <>
      <div
        className={styles.fieldRow}
        style={{ gridTemplateColumns: GRID_COLUMNS }}
      >
        <div
          className={styles.fieldContent}
          style={{ paddingLeft: depth * 20 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={handleExpand}
              className={styles.expandButton}
            >
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </button>
          ) : (
            <span className={styles.expandSpacer} />
          )}
          <CheckboxPrimitive.Root
            checked={checkedValue}
            onCheckedChange={handleCheck}
            className={styles.checkbox}
          >
            <CheckboxPrimitive.Indicator className={styles.checkboxIndicator}>
              {checkState === "indeterminate" ? (
                <Minus className={styles.iconSmall} />
              ) : (
                <Check className={styles.iconMedium} />
              )}
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
          <Text size="2">{node.key}</Text>
        </div>
        <Text size="2" color="gray">
          {node.type}
        </Text>
        <Text size="2" color="gray" truncate>
          {node.sampleValue}
        </Text>
      </div>
      {isExpanded &&
        node.children?.map((child) => (
          <FieldRow
            key={child.path}
            node={child}
            selected={selected}
            onToggle={onToggle}
            depth={depth + 1}
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            searchQuery={searchQuery}
          />
        ))}
    </>
  );
}

export function ResponseFieldTree({
  data,
  selectedFields,
  onFieldsChange,
}: ResponseFieldTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const tree = useMemo(() => {
    if (data == null || typeof data !== "object") return [];
    const source = Array.isArray(data)
      ? typeof data[0] === "object" && data[0] != null
        ? (data[0] as Record<string, unknown>)
        : null
      : (data as Record<string, unknown>);
    if (!source) return [];
    return Object.entries(source)
      .map(([k, v]) => buildTree(v, k, k))
      .filter((n): n is TreeNode => n != null);
  }, [data]);

  const selectedSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const handleToggle = useCallback(
    (node: TreeNode) => {
      const leaves = getLeafPaths(node);
      const allSelected = leaves.every((p) => selectedSet.has(p));
      const next = new Set(selectedSet);
      if (allSelected) {
        leaves.forEach((p) => next.delete(p));
      } else {
        leaves.forEach((p) => next.add(p));
      }
      onFieldsChange(Array.from(next));
    },
    [selectedSet, onFieldsChange]
  );

  const handleToggleCollapse = useCallback((path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const lowerQuery = useMemo(
    () => searchQuery.toLowerCase(),
    [searchQuery]
  );

  return (
    <div>
      <TextField.Root
        placeholder="Search fields..."
        value={searchQuery}
        onChange={handleSearchChange}
        size="1"
        className={styles.searchField}
      >
        <TextField.Slot>
          <MagnifyingGlassIcon height="14" width="14" />
        </TextField.Slot>
      </TextField.Root>

      <div className={styles.treeContainer}>
        <div
          className={styles.headerRow}
          style={{ gridTemplateColumns: GRID_COLUMNS }}
        >
          <Text size="1" weight="medium" color="gray">
            Field
          </Text>
          <Text size="1" weight="medium" color="gray">
            Type
          </Text>
          <Text size="1" weight="medium" color="gray">
            Value (sample)
          </Text>
        </div>
        {tree.map((node) => (
          <FieldRow
            key={node.path}
            node={node}
            selected={selectedSet}
            onToggle={handleToggle}
            depth={0}
            collapsed={collapsed}
            onToggleCollapse={handleToggleCollapse}
            searchQuery={lowerQuery}
          />
        ))}
      </div>
    </div>
  );
}
